import { Box, Cyl, RBox, Slab } from '../../render/parts'
import { CableTray, DESK_HEIGHT, Grommet, TOP_THICKNESS, WorkTop } from '../parts/worktop'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Sit-stand desk: one 1.4 x 0.7 top on two telescopic columns with T-feet.
 * The variant drives the whole frame — the inner column simply grows and the
 * top, the cross beam and the controller ride up with it.
 */

const WIDTH = 1.4
const DEPTH = 0.7
const SIT_HEIGHT = DESK_HEIGHT
const STAND_HEIGHT = 1.12

const LEG_X = 0.5
const FOOT_DEPTH = 0.6
/** Fixed lower column; the inner one telescopes out of it. */
const OUTER_BOTTOM = 0.05
const OUTER_HEIGHT = 0.44
const INNER_BOTTOM = 0.2

/** T-foot, outer column and inner column for one side of the frame. */
function Column({ x, top }: { x: number; top: number }) {
  // Bracket overlaps the underside of the top by a few mm so nothing is coplanar.
  const bracketY = top - 0.04
  const innerTop = bracketY - 0.008
  const innerHeight = innerTop - INNER_BOTTOM

  return (
    <group position={[x, 0, 0]}>
      {[-1, 1].map((side) => (
        <Cyl key={side} tone="darkDeep" r={0.018} h={0.012} segments={12} position={[0, 0.006, side * 0.26]} />
      ))}
      <Slab tone="metal" size={[0.09, FOOT_DEPTH, 0.05]} radius={0.02} position={[0, 0.035, 0]} />
      <RBox
        tone="metal"
        size={[0.09, OUTER_HEIGHT, 0.09]}
        radius={0.008}
        position={[0, OUTER_BOTTOM + OUTER_HEIGHT / 2, 0]}
      />
      <RBox
        tone="metal"
        size={[0.066, innerHeight, 0.066]}
        radius={0.006}
        position={[0, INNER_BOTTOM + innerHeight / 2, 0]}
      />
      <Slab tone="metal" size={[0.08, 0.5, 0.032]} radius={0.012} position={[0, bracketY, 0]} />
    </group>
  )
}

function DeskStanding({ accent, variant = 'sentado' }: AssetContext) {
  const top = variant === 'de-pie' ? STAND_HEIGHT : SIT_HEIGHT
  const controllerY = top - TOP_THICKNESS - 0.01

  return (
    <group>
      <WorkTop width={WIDTH} depth={DEPTH} height={top} />

      <Column x={-LEG_X} top={top} />
      <Column x={LEG_X} top={top} />

      {/* Cross beam, buried in both inner columns. */}
      <Box tone="metal" size={[LEG_X * 2 - 0.02, 0.05, 0.06]} position={[0, top - 0.11, 0]} />
      <CableTray width={WIDTH} deskHeight={top} />

      {/* Height controller hanging under the front edge, with an accent display. */}
      <RBox tone="darkSoft" size={[0.15, 0.024, 0.06]} radius={0.006} position={[0.44, controllerY, 0.26]} />
      <Slab
        tone="accent"
        color={accent}
        size={[0.09, 0.014, 0.004]}
        radius={0.003}
        rotation={[Math.PI / 2, 0, 0]}
        position={[0.44, controllerY, 0.2885]}
      />

      <Grommet x={-0.46} z={-0.24} deskHeight={top} r={0.03} />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'desk-standing',
  name: 'Escritorio elevable',
  category: 'desks',
  footprint: [WIDTH, DEPTH],
  height: STAND_HEIGHT,
  surfaceHeight: SIT_HEIGHT,
  seats: 1,
  occupantSide: 'front',
  accentable: true,
  variants: [
    { id: 'sentado', name: 'Sentado' },
    { id: 'de-pie', name: 'De pie' },
  ],
  keywords: [
    'escritorio elevable',
    'escritorio regulable',
    'de pie',
    'standing desk',
    'sit stand',
    'height adjustable',
  ],
  Component: DeskStanding,
}
